import { displayDigits } from '../../utils/displayDigits';
import React from 'react';
import { IoSpeedometerOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { useLongPress } from '../../hooks/useLongPress';
import { presentHealthMetricName } from '../../utils/healthMetricPresentation';

export interface CalendarMetricSummary {
  unique_code?: string;
  created_at?: string;
  metric_type_name?: string;
  value?: string | number;
  item_count?: number;
}

interface Props {
  metric: CalendarMetricSummary;
  onPress: () => void;
  onLongPress: () => void;
  dragProps?: {
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    'data-dragging'?: string;
  };
}

/** Metric row with speedometer icon — mirrors Ai-Tempo calendar metric card. */
export function CalendarMetricRow({ metric, onPress, onLongPress, dragProps }: Props) {
  const { t, i18n } = useTranslation();
  const { bind, wrapClick } = useLongPress(onLongPress);
  const grouped = Boolean(metric.item_count);
  const metricName = presentHealthMetricName(metric.metric_type_name, i18n.resolvedLanguage ?? i18n.language);

  return (
    <button
      type="button"
      className={`calendar-metric-row${grouped ? ' calendar-metric-row--grouped' : ''}${dragProps?.['data-dragging'] === 'true' ? ' calendar-metric-row--dragging' : ''}`}
      onClick={wrapClick(onPress)}
      {...bind}
      {...dragProps}
    >
      <span className="calendar-metric-row__body">
        <span className="calendar-metric-row__icon" aria-hidden>
          <IoSpeedometerOutline size={16} />
        </span>
        <span className="calendar-metric-row__content">
          {grouped ? <strong>{t('calendar.metricCard.resultsTitle')}</strong> : null}
          <span className="calendar-metric-row__value" dir="auto">
            <span>{metricName}</span>
            <b>{displayDigits(metric.value ?? '—')}</b>
          </span>
        </span>
        {metric.item_count ? (
          <span className="calendar-metric-row__count">
            {displayDigits(t('calendar.metricCard.moreItems', { count: metric.item_count }))}
          </span>
        ) : null}
      </span>
      {metric.created_at ? <span className="calendar-metric-row__time">{displayDigits(metric.created_at)}</span> : null}
    </button>
  );
}
